import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus } from 'lucide-react'
import { Timesheet } from '@/types'
import { timesheetService, WeeklyStats, CurrentWeekEntries, SubmitTimesheetRequest, UnsubmittedWeek, timesheetUtils } from '@/services/timesheetService'
import { useTimer } from '@/contexts/TimerContext'
import WeeklyOverview from './components/WeeklyOverview'
import SubmitTimesheetModal from './components/SubmitTimesheetModal'
import TimesheetTable from './components/TimesheetTable'
import UnsubmittedWeeksSection from './components/UnsubmittedWeeksSection'

const TimesheetsPage: React.FC = () => {
  const navigate = useNavigate()
  const { refreshTimerData } = useTimer()
  const [weeklyStats, setWeeklyStats] = useState<WeeklyStats | null>(null)
  const [timesheets, setTimesheets] = useState<Timesheet[]>([])
  const [currentWeekEntries, setCurrentWeekEntries] = useState<CurrentWeekEntries | null>(null)
  const [unsubmittedWeeks, setUnsubmittedWeeks] = useState<UnsubmittedWeek[]>([])
  const [loading, setLoading] = useState(true)
  const [unsubmittedLoading, setUnsubmittedLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [showSubmitModal, setShowSubmitModal] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // 載入數據
  const loadData = async () => {
    try {
      setLoading(true)
      setError(null)

      const [statsData, timesheetsData] = await Promise.all([
        timesheetService.getWeeklyStats(),
        timesheetService.getTimesheets({ limit: 20 })
      ])

      setWeeklyStats(statsData)
      setTimesheets(timesheetsData.data)
    } catch (error) {
      console.error('Load data error:', error)
      setError('載入數據失敗，請稍後再試')
    } finally {
      setLoading(false)
    }
  }

  // 載入未申報週次
  const loadUnsubmittedWeeks = async () => {
    try {
      setUnsubmittedLoading(true)
      const data = await timesheetService.getUnsubmittedWeeks()
      setUnsubmittedWeeks(data)
    } catch (error) {
      console.error('Load unsubmitted weeks error:', error)
    } finally {
      setUnsubmittedLoading(false)
    }
  }

  // 處理創建申報草稿
  const handleCreateTimesheet = async (weekStart: string) => {
    try {
      setSubmitting(true)
      const newTimesheet = await timesheetService.createDraftTimesheet(weekStart)
      
      // 重新載入數據
      await Promise.all([loadData(), loadUnsubmittedWeeks()])
      
      // 跳轉到編輯頁面
      navigate(`/timesheets/${newTimesheet.id}/edit`)
      
      alert('申報草稿創建成功！正在跳轉到編輯頁面...')
    } catch (error) {
      console.error('Create timesheet error:', error)
      alert('創建申報失敗，請稍後再試')
    } finally {
      setSubmitting(false)
    }
  }

  // 載入本週工時明細
  const loadCurrentWeekEntries = async () => {
    try {
      const data = await timesheetService.getCurrentWeekEntries()
      setCurrentWeekEntries(data)
    } catch (error) {
      console.error('Load current week entries error:', error)
    }
  }

  // 處理提交本週工時
  const handleSubmitCurrentWeek = async (data: SubmitTimesheetRequest) => {
    try {
      setSubmitting(true)
      await timesheetService.submitCurrentWeek(data)
      
      // 重新載入數據
      await loadData()
      setShowSubmitModal(false)
      
      // 刷新計時器頁面數據（包括週次鎖定狀態）
      await refreshTimerData()
      
      // 顯示成功消息
      alert('工時申報提交成功！')
    } catch (error) {
      console.error('Submit timesheet error:', error)
      alert('提交失敗，請稍後再試')
    } finally {
      setSubmitting(false)
    }
  }

  // 處理查看工時申報
  const handleViewTimesheet = (timesheet: Timesheet) => {
    navigate(`/timesheets/${timesheet.id}`)
  }

  // 處理編輯工時申報
  const handleEditTimesheet = (timesheet: Timesheet) => {
    navigate(`/timesheets/${timesheet.id}/edit`)
  }

  // 處理重新提交工時申報
  const handleResubmitTimesheet = (timesheet: Timesheet) => {
    navigate(`/timesheets/${timesheet.id}/edit`)
  }

  // 處理撤回工時申報
  const handleWithdrawTimesheet = async (timesheet: Timesheet) => {
    const confirmed = window.confirm('確定要撤回這個工時申報嗎？撤回後申報狀態將變為草稿，您可以重新編輯並提交。')
    
    if (!confirmed) return

    try {
      setSubmitting(true)
      await timesheetService.withdrawTimesheet(timesheet.id)
      
      // 重新載入數據
      await loadData()
      
      // 刷新計時器頁面數據（包括週次鎖定狀態）
      await refreshTimerData()
      
      // 顯示成功消息
      alert('工時申報撤回成功！')
    } catch (error) {
      console.error('Withdraw timesheet error:', error)
      alert('撤回失敗，請稍後再試')
    } finally {
      setSubmitting(false)
    }
  }

  // 打開提交彈窗
  const handleOpenSubmitModal = async () => {
    await loadCurrentWeekEntries()
    setShowSubmitModal(true)
  }

  useEffect(() => {
    loadData()
    loadUnsubmittedWeeks()
  }, [])

  if (error) {
    return (
      <div className="space-y-6">
        <h1 className="text-3xl font-bold text-secondary-800">工時申報</h1>
        <div className="card p-8 text-center">
          <div className="text-red-500 mb-4">
            <svg className="w-16 h-16 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold mb-2 text-gray-900">載入失敗</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button onClick={loadData} className="btn-primary">
            重新載入
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-secondary-800">工時申報</h1>
        <div className="flex items-center space-x-3">
          <button
            onClick={handleOpenSubmitModal}
            className="btn-primary flex items-center space-x-2"
            disabled={loading || !weeklyStats || weeklyStats.pendingSubmissions === 0}
          >
            <Plus className="w-4 h-4" />
            <span>補提交歷史</span>
          </button>
        </div>
      </div>

      {/* 未申報週次緊急提醒 */}
      <UnsubmittedWeeksSection
        unsubmittedWeeks={unsubmittedWeeks}
        onCreateTimesheet={handleCreateTimesheet}
        loading={unsubmittedLoading}
      />

      {/* 本週工時提交區 */}
      {weeklyStats && weeklyStats.pendingSubmissions > 0 && (
        <div className="card p-6 bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
          <div className="flex flex-col space-y-4 md:flex-row md:items-center md:justify-between md:space-y-0">
            <div className="flex-1 text-center md:text-left">
              <h2 className="text-lg font-semibold text-gray-900 mb-2">
                ⚡ 本週工時提交 ({timesheetUtils.formatWeekRange(weeklyStats.weekStart, weeklyStats.weekEnd)})
              </h2>
              <p className="text-gray-600">
                📊 本週已記錄 {weeklyStats.weeklyTotalHours} 小時，共 {weeklyStats.workingDays} 個工作日
              </p>
            </div>
            <div className="flex justify-center md:justify-start">
              <button
                onClick={handleOpenSubmitModal}
                className="btn-primary px-8 py-3 w-full md:w-auto"
              >
                ✅ 提交本週工時申報
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 本週統計概覽 */}
      <div>
        <h2 className="text-xl font-semibold text-gray-900 mb-4">📈 本週統計概覽</h2>
        <WeeklyOverview stats={weeklyStats} loading={loading} />
      </div>

      {/* 品質分數說明 */}
      <div className="card p-4 bg-gray-50">
        <div className="text-sm text-gray-600">
          <span className="font-medium">💡 品質分數說明：</span>
          <span className="ml-2">70-80分：需要改善</span>
          <span className="mx-2">•</span>
          <span>80-90分：符合預期</span>
          <span className="mx-2">•</span>
          <span>90-100分：傑出</span>
        </div>
      </div>

      {/* 申報記錄 */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900">📋 申報記錄</h2>
          <div className="flex items-center space-x-3">
            <select className="form-select text-sm">
              <option value="">全部狀態</option>
              <option value="DRAFT">草稿</option>
              <option value="SUBMITTED">審核中</option>
              <option value="APPROVED">已核准</option>
              <option value="REJECTED">已駁回</option>
            </select>
            <button className="btn-ghost text-sm">
              匯出
            </button>
          </div>
        </div>

        <TimesheetTable
          timesheets={timesheets}
          loading={loading}
          onView={handleViewTimesheet}
          onEdit={handleEditTimesheet}
          onResubmit={handleResubmitTimesheet}
          onWithdraw={handleWithdrawTimesheet}
        />
      </div>

      {/* 分頁 */}
      {timesheets.length > 0 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-500">
            📄 顯示 1-{timesheets.length} 筆，共 {timesheets.length} 筆記錄
          </div>
          <div className="flex items-center space-x-2">
            <button className="btn-ghost text-sm" disabled>
              上一頁
            </button>
            <button className="btn-ghost text-sm" disabled>
              下一頁
            </button>
          </div>
        </div>
      )}

      {/* 提交工時彈窗 */}
      <SubmitTimesheetModal
        isOpen={showSubmitModal}
        onClose={() => setShowSubmitModal(false)}
        onSubmit={handleSubmitCurrentWeek}
        weekEntries={currentWeekEntries}
        loading={submitting}
      />
    </div>
  )
}

export default TimesheetsPage
